/**
 * ============================================================
 *   人才盘点 · 个人报告预览组件 —— 已定稿模板（v1.0 · 2026-04-30）
 * ============================================================
 *
 * 屏幕层级（2026-07-14 Round-2 信息权重版）：
 *   锚点长页：画像(含人才判断) → 指标（含素质项评价） → 依据（含培养建议及行动计划）
 *   （素质 TOP / 雷达已并入依据区「素质项评价」标题下；AI 判断区块已下线）
 *
 * 修改前必须找业务方（秦艳芳）review；PDF 保持原版式，后续另行同步。
 * ============================================================
 */
import React, { useMemo } from 'react';
import { Anchor, Button, Divider, Space, Tag, Typography } from 'antd';
import type { PersonalReportSummaryJson } from '../data/mockPersonalReportSummary';
import { EmployeeProfileCard } from './report/EmployeeProfileCard';
import { EvidenceDetail } from './report/EvidenceDetail';
import { MetricOverview } from './report/MetricOverview';
import { buildMultiViewProfile } from './report/multiViewProfile';
import { computeEffectiveRadar } from './report/shared';
import './reportEditorial.css';

const ANCHOR_ITEMS = [
  { key: 'profile', href: '#profile', title: '画像' },
  { key: 'metrics', href: '#metrics', title: '指标' },
  { key: 'evaluation', href: '#evaluation', title: '素质' },
  { key: 'development', href: '#development', title: '培养' },
  { key: 'evidence', href: '#evidence', title: '依据' },
];

function ReportSectionHeader({ title }: { title: string }) {
  return (
    <div className="report-section-header">
      <Typography.Title level={4} className="report-section-header-title">
        {title}
      </Typography.Title>
      <Divider className="report-section-header-divider" />
    </div>
  );
}

export function PersonalReportPreview({
  data,
  titleSuffix = '示例预览',
  variant = 'report',
  onGenerateNarrative,
  generateLoading = false,
}: {
  data: PersonalReportSummaryJson;
  titleSuffix?: string;
  variant?: 'sample' | 'report';
  onGenerateNarrative?: () => void;
  generateLoading?: boolean;
}) {
  const { evaluationBreakdown } = data;

  const effectiveRadar = useMemo(
    () => computeEffectiveRadar(data.radar, evaluationBreakdown),
    [data.radar, evaluationBreakdown],
  );

  const multiViewProfile = useMemo(
    () => buildMultiViewProfile(evaluationBreakdown),
    [evaluationBreakdown],
  );

  const aiQueueStatus = data.aiQueueStatus ?? (data.aiNarrative ? 'READY' : 'NONE');
  const aiBusy = aiQueueStatus === 'PENDING' || aiQueueStatus === 'RUNNING';
  const aiStatusLabel = !data.aiEnabled
    ? 'AI 未开通'
    : aiBusy
      ? aiQueueStatus === 'PENDING'
        ? 'AI 排队中'
        : 'AI 生成中'
      : aiQueueStatus === 'FAILED'
        ? 'AI 生成失败'
        : data.aiNarrative
          ? 'AI 已生成'
          : 'AI 未生成';

  return (
    <div className="personal-report-preview report-editorial">
      {variant === 'report' ? (
        <div className="report-toolbar">
          <Space wrap>
            <Tag color={data.aiNarrative ? 'green' : aiQueueStatus === 'FAILED' ? 'error' : 'default'}>
              {aiStatusLabel}
            </Tag>
            {data.aiEnabled && onGenerateNarrative && data.aiNarrative ? (
              <Button
                size="small"
                loading={generateLoading || aiBusy}
                disabled={aiBusy}
                onClick={onGenerateNarrative}
              >
                重新生成 AI 摘要
              </Button>
            ) : null}
          </Space>
        </div>
      ) : null}

      <div className="report-stacked-layout">
        <div className="report-stacked-sections">
          <EmployeeProfileCard data={data} titleSuffix={titleSuffix} />

          <ReportSectionHeader title="核心指标" />
          <MetricOverview
            data={data}
            effectiveRadar={effectiveRadar}
            multiViewProfile={multiViewProfile}
          />

          <ReportSectionHeader title="评价依据" />
          <EvidenceDetail
            data={data}
            effectiveRadar={effectiveRadar}
            multiViewProfile={multiViewProfile}
            onGenerateNarrative={onGenerateNarrative}
            generateLoading={generateLoading}
          />
        </div>

        <aside className="report-anchor-rail" aria-label="页面导航">
          <Anchor
            offsetTop={64}
            targetOffset={72}
            // 主内容区独立滚动（.tg-app-content），不能用默认 window
            getContainer={() =>
              (document.querySelector('.tg-app-content') as HTMLElement | null) ??
              window
            }
            items={ANCHOR_ITEMS}
            className="report-page-anchor"
          />
        </aside>
      </div>

      <Divider />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {variant === 'sample'
          ? '瑞小美人才盘点'
          : '瑞小美人才盘点 · 报告含水印与操作者信息，请勿外传。'}
      </Typography.Text>
    </div>
  );
}
